import { CrewTimelineEntryType } from '../../../../../../common/types/timeline/CrewTimelineEntryType';
import { AircraftTimelineEntryType } from '../../../../../../common/types/timeline/AircraftTimelineEntryType';
import { ItemContext } from './types';
import { CrewTimelineItem } from './CrewTimeline/generator';
import { AircraftTimelineItem } from './AircraftTimeline/generator';
import colors from '../colors';
import { useLayoutEffect, useState } from 'react';
import { TIMELINE_HEIGHT, TIMELINE_HEIGHT_CORRECTION } from './constants';
import { ContractTimelineItem } from './ContractTimeline/generator';
import { ContractTimelineEntryType } from '../../../../../../common/types/timeline/ContractTimelineEntryType';
import { MaintenanceTimelineEntryType } from '../../../../../../common/types/timeline/MaintenanceTimelineEntryType';
import { MaintenanceTimelineItem } from './MaintenanceTimeline/generator';
import { CompanySettings } from '../../../../../../common/types/Company';

export const buildGroupID = (
  entryID: string,
  entryType:
    | CrewTimelineEntryType
    | AircraftTimelineEntryType
    | ContractTimelineEntryType
    | MaintenanceTimelineEntryType,
): string => {
  return `${entryID}__${entryType}`;
};

export const parseGroupID = (
  groupID: string,
): {
  entryID: string;
  entryType:
    | CrewTimelineEntryType
    | AircraftTimelineEntryType
    | ContractTimelineEntryType
    | MaintenanceTimelineEntryType;
} => {
  const parts = groupID.split('__');
  if (parts.length !== 2) {
    throw new Error('Invalid group ID format');
  }

  const entryID = parts[0];
  const entryType = parts[1] as
    | CrewTimelineEntryType
    | AircraftTimelineEntryType
    | ContractTimelineEntryType
    | MaintenanceTimelineEntryType;

  return {
    entryID,
    entryType,
  };
};

export const getBackgroundColor = (
  item: CrewTimelineItem | AircraftTimelineItem | ContractTimelineItem | MaintenanceTimelineItem,
  itemContext: ItemContext,
  selectedItems: (CrewTimelineItem | AircraftTimelineItem | MaintenanceTimelineItem)[] = [],
  contractName?: string,
) => {
  if (selectedItems && selectedItems.map(it => it.id).includes(item.id)) {
    return colors.timeline.darkYellow;
  }

  if (item.isNewEntry) {
    return colors.timeline.blueBackground;
  }

  if (itemContext.selected && selectedItems.length === 0) {
    return itemContext.dragging ? colors.timeline.red : colors.timeline.selectedYellow;
  }

  if (contractName === 'Day Off') {
    return colors.timeline.red;
  }

  // @ts-ignore
  if (item.baseScheduleDetails) {
    // @ts-ignore
    if (item.baseScheduleDetails?.Routing) {
      // @ts-ignore
      if (item.status === 'confirmed') {
        return '#008000';
      }
      return '#FEE12B';
    }
  }

  return item.bgColor;
};

export const getBorder = (
  item: CrewTimelineItem | ContractTimelineItem | MaintenanceTimelineItem,
  itemContext: ItemContext,
  selectedItems?: (AircraftTimelineItem | CrewTimelineItem | MaintenanceTimelineItem)[],
) => {
  const crewEntry = (item as CrewTimelineItem).crewEntry;
  const dutyEntry = (item as CrewTimelineItem).dutyEntry;
  const maintenanceEntry = (item as MaintenanceTimelineItem).maintenanceEntry;
  const isUnpublished =
    crewEntry?.IsUnpublished || dutyEntry?.IsUnpublished || maintenanceEntry?.IsUnpublished;
  let border = '';
  if (item.isNewEntry) {
    border = '1px solid black';
  } else if (itemContext.selected && selectedItems?.length === 0) {
    border = '1px solid red';
  } else if (isUnpublished) {
    border = `3px dashed ${
      item.bgColor !== colors.timeline.yellow ? colors.timeline.yellow : colors.timeline.black
    }`;
  } else {
    if (item.entryType === CrewTimelineEntryType.CrewSchedule) {
      border = `1px solid ${item.crewEntry.BorderColor || colors.timeline.darkBlue}`;
    } else {
      border = `1px solid ${colors.timeline.darkBlue}`;
    }
  }
  return border;
};

export function useWindowSize() {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

export function calcTimelineHeight(
  windowHeight: number,
  isShortHeader: boolean,
  visibleTimelinesAmount: number,
  rowsLen: number,
  rowHeight: number,
): number {
  const topHeight = (windowHeight - TIMELINE_HEIGHT_CORRECTION) / visibleTimelinesAmount;
  const headerHeight = isShortHeader ? 23 : 62;
  let calculatedHeight = rowHeight * rowsLen;
  let resultedHeight = TIMELINE_HEIGHT;
  if (calculatedHeight < TIMELINE_HEIGHT) {
    resultedHeight = calculatedHeight + headerHeight;
  } else if (topHeight > resultedHeight) {
    resultedHeight = topHeight - headerHeight;
  }
  return resultedHeight;
}

export const getTextColor = (
  item: CrewTimelineItem | AircraftTimelineItem | ContractTimelineItem | MaintenanceTimelineItem,
  itemContext: ItemContext,
) => {
  if (item.entryType === CrewTimelineEntryType.CrewSchedule) {
    return item?.crewEntry?.TextColor || colors.timeline.white;
  } else if (item.entryType === MaintenanceTimelineEntryType.MaintenanceCrew) {
    return item?.maintenanceEntry?.TextColor || colors.timeline.white;
  } else {
    return colors.timeline.white;
  }
};

export const getPositionLabel = (companySettings: CompanySettings, position: string) => {
  if (companySettings.UserCertifications?.length > 0) {
    const certification = companySettings.UserCertifications.find(c =>
      c.Assignment.some(a => a.Name === position),
    );

    if (certification) {
      const assignment = certification.Assignment.find(a => a.Name === position);
      if (assignment) {
        return assignment.Label;
      }
    }
  }

  return position;
};
